const STORAGE_KEY = 'echo-diary:state:v1';

export const SAMPLE_CARDS = [
  '答案没有离开，它只是换了一种更安静的方式靠近你。',
  '再等一等，但不要停在原地。',
  '你正在保护的，也许正是困住你的东西。',
  '门没有锁，只是你一直没有推开。',
  '有些沉默不是拒绝，而是事情仍在生长。',
  '别把熟悉误认为命中注定。',
  '现在不需要决定永远，只需要决定下一步。',
  '真正的转机，通常先以不安的样子出现。',
  '你可以回头确认方向，但不必重新住进过去。',
  '如果答案让你立刻安心，也许它只是你最想听见的话。',
  '暂时放下证明自己的念头，你会看见更多。',
  '这一次，请把你的感受也算进答案里。',
  '事情并非没有变化，只是变化还没有发出声音。',
  '不要急着追赶，先确认那是不是你想去的方向。',
  '你已经知道该舍弃什么，只是仍在等待一个理由。',
  '允许今天没有结论。空白也是这段故事的一部分。',
  '你担心失去的，未必真的属于你。',
  '先照顾那个一直假装没事的自己。',
];

export const DEFAULT_SETTINGS = Object.freeze({
  notebookOwner: '',
  displaySpeed: 'normal',
  answerHoldDuration: 15000,
  inputMode: 'all',
  theme: 'system',
});

export function createDefaultState() {
  return {
    version: 3,
    customReplies: [],
    customReplyGroups: [],
    disabledReplyItems: [],
    settings: { ...DEFAULT_SETTINGS },
  };
}

export function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultState();
    return sanitizeState(JSON.parse(raw));
  } catch (error) {
    console.warn('[Book字卡] 无法读取本地字库，使用空字库和默认设置。', error);
    return createDefaultState();
  }
}

export function saveState(state) {
  const safe = sanitizeState(state);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  return safe;
}

export function resetToSamples(currentState) {
  return {
    ...createDefaultState(),
    customReplies: [...SAMPLE_CARDS],
    customReplyGroups: [
      {
        id: 'starter-echoes',
        name: '示例字卡',
        color: '#8b5848',
        disabled: false,
        items: [...SAMPLE_CARDS],
      },
    ],
    settings: sanitizeSettings(currentState?.settings),
  };
}

export function sanitizeState(candidate) {
  const state = candidate && typeof candidate === 'object' ? candidate : {};
  const customReplies = Array.isArray(state.customReplies)
    ? state.customReplies.filter((item) => typeof item === 'string')
    : [];
  const customReplyGroups = Array.isArray(state.customReplyGroups)
    ? state.customReplyGroups.map(sanitizeGroup).filter(Boolean)
    : [];
  const disabledReplyItems = Array.isArray(state.disabledReplyItems)
    ? state.disabledReplyItems.filter((item) => typeof item === 'string')
    : [];
  const rawSettings = state.settings && typeof state.settings === 'object' ? state.settings : {};

  return {
    version: 3,
    customReplies,
    customReplyGroups,
    disabledReplyItems,
    settings: sanitizeSettings(rawSettings),
  };
}

function sanitizeGroup(group, index) {
  if (!group || typeof group !== 'object') return null;
  return {
    id: group.id ?? `group-${Date.now()}-${index}`,
    name: String(group.name || `未命名分组 ${index + 1}`).trim().slice(0, 40),
    color: /^#[0-9a-f]{6}$/i.test(group.color) ? group.color : '#7a6854',
    disabled: Boolean(group.disabled),
    items: Array.isArray(group.items) ? group.items.filter((item) => typeof item === 'string') : [],
  };
}

function sanitizeSettings(settings) {
  const raw = settings && typeof settings === 'object' ? settings : {};
  return {
    notebookOwner: sanitizeNotebookOwner(raw.notebookOwner),
    displaySpeed: resolveDisplaySpeed(raw),
    answerHoldDuration: nearestHoldDuration(raw.answerHoldDuration),
    inputMode: ['all', 'pencil'].includes(raw.inputMode) ? raw.inputMode : DEFAULT_SETTINGS.inputMode,
    theme: ['system', 'light', 'dark'].includes(raw.theme) ? raw.theme : DEFAULT_SETTINGS.theme,
  };
}

function sanitizeNotebookOwner(value) {
  if (typeof value !== 'string') return DEFAULT_SETTINGS.notebookOwner;
  return value.replace(/\s+/g, ' ').trim().slice(0, 40);
}

function resolveDisplaySpeed(settings) {
  if (['slow', 'normal', 'fast'].includes(settings.displaySpeed)) return settings.displaySpeed;
  if (settings.tempo === 'quick') return 'fast';
  if (settings.tempo === 'slow') return 'slow';
  const legacySpeed = Number(settings.strokeAnimationSpeed);
  if (Number.isFinite(legacySpeed) && legacySpeed >= 3) return 'fast';
  if (Number.isFinite(legacySpeed) && legacySpeed <= 1.5) return 'slow';
  return DEFAULT_SETTINGS.displaySpeed;
}

function nearestHoldDuration(value) {
  const options = [0, 10000, 15000, 30000];
  const number = Number(value);
  if (!Number.isFinite(number)) return DEFAULT_SETTINGS.answerHoldDuration;
  return options.reduce((closest, option) => (
    Math.abs(option - number) < Math.abs(closest - number) ? option : closest
  ), DEFAULT_SETTINGS.answerHoldDuration);
}
